import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {Link} from 'react-router-dom'
import {useToolAccess} from '../lib/toolAccess.jsx'
import PageHeader from '../components/PageHeader.jsx'
import SchoolTeamBook from '../features/bowl/SchoolTeamBook.jsx'
import { IconCheck, IconSearch } from '../components/icons.jsx'
import { api } from '../lib/api.js'
import { creationWasRejected, verifiedTeamMember } from '../lib/teamAccountVerification.js'
import TeamVisibilityPanel from './TeamVisibilityPanel.jsx'
import ClickupIdentityPanel from '../features/team/ClickupIdentityPanel.jsx'
import { useAccess } from '../lib/access.jsx'
import { useAuth } from '../lib/auth.jsx'
import { ACTIONS, ROLE_META, ROLES, can as rawCan, makeContext } from '../lib/rbac.js'
import {
  MANAGE_ASSIGNMENTS, MANAGE_ASSIGNMENTS_LABEL, TOGGLEABLE_ROLES, ROLE_LABEL,
  resolveManageAssignments, sourceLabel, grantedByBackend, userSubject, roleSubject,
} from '../lib/assignmentPermissions.js'
import { LANE_DELEGATE_LABEL, readLane } from '../features/command-center/laneDelegation.js'
import styles from './TeamManagement.module.css'

/**
 * TEAM MANAGEMENT — the internal roster, and the screen where a role stops
 * being a label and becomes a grant.
 *
 * Modeled on the Pricing Engine's member-management page (personnel list, role
 * per person, activate/deactivate, add-person and password tools), rebuilt on
 * the real backend instead of localStorage: every row here is a `users` record
 * in sr-portal-backend, and the role set on it is the role that goes on that
 * person's next JWT.
 *
 * The chain this page closes:
 *
 *     this page  →  POST/PATCH /api/admin/team  →  users store
 *                →  current roster role and active status checked on secured requests
 *                →  GET /api/access/me  →  AccessProvider  →  can()
 *
 * GATING. `can(TEAM_MANAGE)` decides what gets drawn — that is UX only, and the
 * comment at the top of lib/rbac.js applies in full. The enforcement is
 * `requireExec` on every /api/admin/team* route, deciding from the verified
 * token. A user who flips their role in devtools sees this page and gets a 403
 * from every button on it.
 */

const ROLE_OPTIONS = [ROLES.EXEC, ROLES.REP, ROLES.OPS, ROLES.MARKETING]

/** A grant store that never answered, as opposed to one that said no. */
function isUnreachable(err) {
  if (err?.missingRoute) return true
  const status = Number(err?.status || 0)
  return !status || status === 404 || status >= 500
}

/** Errors from `request()` are ApiError; a 404 means the route isn't deployed. */
function errText(err, fallback) {
  if (err?.missingRoute) return 'This route is not deployed on the backend yet.'
  return err?.body?.reason || err?.body?.detail || err?.body?.error || err?.message || fallback
}

export function TeamMemberToolAccessLink({member}){
  const access=useToolAccess()
  return access?.data?.canManage&&member.active===true?<Link className="btn" to={`/tool-access?person=${encodeURIComponent(member.id)}`} aria-label={`Tool access for ${member.name}`}>Tool access</Link>:null
}

export default function TeamManagement() {
  const { can, why, role, setAssignmentGrant, refreshPermissions } = useAccess()
  const { user } = useAuth()
  const allowed = can(ACTIONS.TEAM_MANAGE)
  // Bumped on every Manage Assignments flip so the column re-resolves. The
  // overrides live in `lib/assignmentPermissions.js`, not in this page.
  const [grantTick, setGrantTick] = useState(0)

  const [state, setState] = useState({ status: 'loading', members: [], bookSource: null, error: '' })
  /* THE GRANT STORE, read directly. GET /api/permissions is where
   * `cc_rep_assign` and `cc_lane_delegate` live; the roster rows from
   * /api/admin/team do not carry them. `null` means it did not answer, and the
   * column then falls back to the old resolution and says so per row. */
  const [grants, setGrants] = useState({ byId: null, lane: null, error: '' })
  const [filter, setFilter] = useState('')
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState('')
  const [bookFor, setBookFor] = useState(null)

  const flash = useCallback((tone, text) => {
    setNotice({ tone, text })
    setTimeout(() => setNotice((n) => (n?.text === text ? null : n)), 6000)
  }, [])

  const loadGrants = useCallback(async () => {
    if (!allowed) return
    try {
      const res = await api.permissionsList()
      const byId = new Map((res?.members || []).map((g) => [String(g.userId || '').toLowerCase(), g]))
      setGrants({ byId, lane: readLane(res?.lane), error: '' })
    } catch (err) {
      setGrants({ byId: null, lane: null, error: errText(err, 'The grant store did not answer.') })
    }
  }, [allowed])

  const load = useCallback(async () => {
    if (!allowed) return
    loadGrants()
    setState((s) => ({ ...s, status: s.members.length ? 'refreshing' : 'loading' }))
    try {
      const res = await api.team()
      setState({
        status: 'ready',
        members: res.members || [],
        bookSource: res.book || null,
        error: '',
      })
    } catch (err) {
      setState((s) => ({ ...s, status: 'error', error: errText(err, 'Could not load the roster.') }))
    }
  }, [allowed, loadGrants])

  useEffect(() => {
    load()
  }, [load])

  const members = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return state.members
    return state.members.filter((m) =>
      [m.name, m.id, m.email, m.role].some((v) => String(v || '').toLowerCase().includes(q))
    )
  }, [state.members, filter])

  const counts = useMemo(() => {
    const out = { total: state.members.length, active: 0 }
    for (const r of ROLE_OPTIONS) out[r] = 0
    for (const m of state.members) {
      if (m.active) out.active += 1
      if (out[m.role] !== undefined) out[m.role] += 1
    }
    return out
  }, [state.members])

  /* -- Mutations. Each one reloads, so the table never drifts from the store. */

  const mutate = useCallback(
    async (key, fn, okText) => {
      setBusy(key)
      try {
        await fn()
        await load()
        flash('ok', okText)
        return true
      } catch (err) {
        flash('danger', errText(err, 'That change did not apply.'))
        return false
      } finally {
        setBusy('')
      }
    },
    [load, flash]
  )

  const setRole = (m, nextRole) =>
    mutate(`role:${m.id}`, () => api.teamUpdate(m.id, { role: nextRole }, { role: m.role }),
      `${m.name} is now ${ROLE_META[nextRole]?.label || nextRole}. Secured requests recheck their current roster role.`)

  const setActive = (m, active) =>
    mutate(`active:${m.id}`, () => api.teamUpdate(m.id, { active }, { active: m.active }),
      active ? `${m.name} reactivated.` : `${m.name} deactivated — sign-in and secured requests reject inactive accounts.`)

  const remove = (m) =>
    mutate(`remove:${m.id}`, () => api.teamRemove(m.id, { id: m.id, role: m.role }),
      `${m.name} removed from the roster.`)

  /* -- MANAGE ASSIGNMENTS ------------------------------------------------
   *
   * The permission that decides who may reassign a rep in the Command Center.
   * It is separate from a role on purpose: Brian switches one person on without
   * making them an executive.
   *
   * The durable write is PUT /api/permissions/:userId, the backend's grant
   * store. ⚠️ Until 2026-10-29 this page PATCHed the roster row with a
   * `permissions` array the route ignored, answered 200 to, and saved nowhere,
   * so "granted on the roster" was printed for a grant that did not exist.
   * A genuine failure still falls back to a session override that says so.
   */

  /** The grant store's row for this member, or null when it did not answer. */
  const grantRow = useCallback(
    (m) => grants.byId?.get(String(m.id || '').toLowerCase()) || null,
    [grants.byId]
  )

  /** How this member's permission resolves right now, and where it came from. */
  const grantFor = useCallback(
    (m) => {
      const row = grantRow(m)
      if (row) {
        const fromRole = row.source === 'role:exec'
        return {
          allowed: row.canManageAssignments === true,
          source: fromRole ? 'role' : row.canManageAssignments ? 'backend' : 'denied',
          persisted: true,
          why: '',
        }
      }
      return resolveManageAssignments({
        user: { id: m.id },
        role: m.role,
        // Their ROLE's own grant, decided by the policy matrix rather than
        // re-derived here.
        policyAllows: rawCan(makeContext({ role: m.role }), MANAGE_ASSIGNMENTS),
        permissions: m.permissions,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [grantTick, grantRow]
  )

  const toggleAssignments = useCallback(async (m, on) => {
    const key = `perm:${m.id}`
    setBusy(key)
    const row = grantRow(m)
    const before = { canManageAssignments: row ? row.canManageAssignments === true : null }
    try {
      await api.teamPermissions(m.id, {
        canManageAssignments: !!on,
        reason: `Set from Team Management by ${user?.name || user?.id || 'an executive'}`,
      }, before)
      await load()
      flash('ok', `${MANAGE_ASSIGNMENTS_LABEL} ${on ? 'granted to' : 'revoked from'} ${m.name}. Saved to the grant store.`)
      refreshPermissions()
    } catch (err) {
      /* ONLY A GRANT STORE THAT COULD NOT BE REACHED falls back to a session
       * override. A refusal with a reason is the server saying no, and holding
       * the grant locally anyway would draw a control it will refuse. */
      if (isUnreachable(err)) {
        setAssignmentGrant(userSubject({ id: m.id }), on)
        setGrantTick((n) => n + 1)
        flash(
          'warn',
          `${MANAGE_ASSIGNMENTS_LABEL} ${on ? 'granted to' : 'revoked from'} ${m.name} for this session only. ` +
          `The grant store did not answer (${errText(err, 'no response')}), so nothing was saved.`
        )
      } else {
        flash('danger', `${MANAGE_ASSIGNMENTS_LABEL} not changed for ${m.name}: ${errText(err, 'the server refused it.')}`)
      }
    } finally {
      setBusy('')
    }
  }, [load, flash, setAssignmentGrant, grantRow, user, refreshPermissions])

  /* -- CHEER LANE DELEGATION ---------------------------------------------
   *
   * Brian, 2026-10-29: "a toggle specific for her so she can only control her
   * 3". Drawn on the lane lead's row only. It does NOT grant Manage
   * Assignments: it lets the lead move cheer records between the lane's
   * members and nothing else, and the backend's assignment lock enforces that
   * on every move. No session fallback: a lane grant that is not in the store
   * does nothing on the server, so pretending otherwise would be the bug this
   * page just had. */
  const toggleLane = useCallback(async (m, on) => {
    const key = `lane:${m.id}`
    setBusy(key)
    const row = grantRow(m)
    try {
      await api.teamPermissions(m.id, {
        canDelegateLane: !!on,
        reason: `Set from Team Management by ${user?.name || user?.id || 'an executive'}`,
      }, { canDelegateLane: row ? row.canDelegateLane === true : null })
      await load()
      flash('ok', `${LANE_DELEGATE_LABEL} ${on ? 'granted to' : 'revoked from'} ${m.name}. Saved to the grant store.`)
      refreshPermissions()
    } catch (err) {
      flash('danger', `${LANE_DELEGATE_LABEL} not changed for ${m.name}: ${errText(err, 'the server refused it.')}`)
    } finally {
      setBusy('')
    }
  }, [load, flash, grantRow, user, refreshPermissions])

  /** The whole-role switch. Always local — there is no role-permission store. */
  const toggleRoleAssignments = useCallback((r, on) => {
    setAssignmentGrant(roleSubject(r), on)
    setGrantTick((n) => n + 1)
    flash('warn', `${MANAGE_ASSIGNMENTS_LABEL} ${on ? 'on' : 'off'} for ${ROLE_LABEL(r)} — this session only.`)
  }, [setAssignmentGrant, flash])

  if (!allowed) {
    return (
      <div className="fade-up">
        <PageHeader
          eyebrow="Access control"
          title="Team Management"
          lead="Personnel, roles, and rep assignments."
        />
        <div className={`card ${styles.denied}`}>
          <div className="eyebrow">Not available to your role</div>
          <p className={styles.deniedText}>{why(ACTIONS.TEAM_MANAGE)}</p>
          <p className={`${styles.deniedText} muted`}>
            Your session is <strong>{ROLE_META[role]?.label || role}</strong>. The roster is limited
            to executives, and the backend enforces the same rule independently — this is not a
            screen that can be unlocked from the browser.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="Access control"
        title="Team Management"
        lead="The internal roster. Everyone who can sign in, the role their session carries, and — for reps — the accounts they are assigned. Role changes and deactivation are checked against the current roster on protected requests."
        actions={
          <button className="btn" onClick={load} disabled={state.status === 'refreshing'}>
            {state.status === 'refreshing' ? <span className="spinner" /> : null}
            Refresh
          </button>
        }
      />

      {notice && (
        <div className={styles.notice} data-tone={notice.tone} role="status">
          {notice.text}
        </div>
      )}

      {/* Roster summary + where the book comes from */}
      <div className={styles.strip}>
        <Stat label="On roster" value={counts.total} sub={`${counts.active} active`} />
        {ROLE_OPTIONS.map((r) => (
          <Stat key={r} label={ROLE_META[r].label} value={counts[r]} sub={ROLE_META[r].lane} />
        ))}
        <div className={styles.stat}>
          <div className="eyebrow">Rep assignment source</div>
          <div className={styles.statValue}>{state.bookSource?.source || '—'}</div>
          <div className={styles.statSub}>
            {state.bookSource?.configured === false
              ? 'not configured in the API env'
              : state.bookSource?.listId
                ? `ClickUp list ${state.bookSource.listId}`
                : state.bookSource?.property || 'derived'}
          </div>
        </div>
      </div>

      {state.status === 'error' && (
        <div className={`card ${styles.denied}`}>
          <div className="eyebrow">Could not load the roster</div>
          <p className={styles.deniedText}>{state.error}</p>
        </div>
      )}

      {/* ---- Team visibility -------------------------------------------- *
        *
        * ⭐ THE EXEC TOGGLE FOR REPS SEEING EACH OTHER. Brian stands in a rep's
        * view to manage; reps stand in each other's to coordinate and compete —
        * and whether the second half is on is his call. Default OFF.
        *
        * ⚠️ THIS IS WHY `api.tamVisibility` EXISTS AT ALL. Those helpers sat
        * undeclared for releases because "a helper with no control is not
        * progress toward a feature; it is surface area that reads as one", and
        * I re-declared them one commit ago and then shipped no control — which
        * `check-doors` reported as NO DOOR in the same build. This is the
        * control; the helpers are no longer dangling.
        *
        * ⚠️ IT SENDS A REAL BOOLEAN. The writer accepts only booleans and the
        * literal strings 'true'/'false' and THROWS on anything else, because
        * `!!'false'` is `true` and a router written the obvious way would have
        * published every rep's book with `?enabled=false`. Confirmed intact
        * 2026-10-04 (`strictBool`). The client sends `=== true`, so the two
        * ends agree rather than relying on one of them.
        *
        * ⚠️ EXEC SEES EVERYTHING REGARDLESS. This switch was never a
        * restriction on Brian, and money stays exec-only whether it is on or
        * off — seeing a colleague's workload is not seeing their pay. */}
      <TeamVisibilityPanel flash={flash} />
      <ClickupIdentityPanel />

      {/* ---- Personnel -------------------------------------------------- */}
      <section className={`card ${styles.panel}`}>
        <div className={styles.panelHead}>
          <div>
            <div className="eyebrow">Personnel</div>
            <h2 className={`${styles.panelTitle} display`}>Who can sign in, and as what</h2>
          </div>
          <label className={styles.search}>
            <IconSearch width={15} height={15} />
            <input
              className={styles.searchInput}
              placeholder="Filter by name, username, email or role"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </label>
        </div>

        {/* Cards below 640px. This was the widest table in the app — 760px of
            content in a 350px viewport — and the role picker, the Manage
            Assignments grant and the Deactivate/Remove buttons all sat past
            the right edge, which is most of the reason to be on this page at
            all. `cc-cards` / `cc-cards-wrap` are the shared hooks; the layout
            itself lives in src/styles/cc-mobile.css. */}
        <div className={`${styles.tableWrap} cc-scrollcue cc-cards-wrap`}>
          <table className={`${styles.table} cc-cards`}>
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Lane</th>
                <th>Book</th>
                {/* WHO MAY REASSIGN A REP. Separate from the role so one person
                    can be switched on without becoming an executive. */}
                <th>{MANAGE_ASSIGNMENTS_LABEL}</th>
                <th>Status</th>
                <th className={styles.right}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.status === 'loading' && (
                <tr>
                  <td colSpan={7} className={styles.empty}>
                    <span className="spinner" /> Loading the roster…
                  </td>
                </tr>
              )}
              {state.status !== 'loading' && !members.length && (
                <tr>
                  <td colSpan={7} className={styles.empty}>
                    {state.members.length ? 'No one matches that filter.' : 'No one on the roster yet.'}
                  </td>
                </tr>
              )}
              {members.map((m) => {
                const self = m.id === user?.id
                const perm = grantFor(m)
                return (
                  <tr key={m.id} data-inactive={!m.active}>
                    <td data-col="name">
                      <div className={styles.person}>
                        <strong>{m.name}</strong>
                        {self && <span className="chip">you</span>}
                      </div>
                      <div className={styles.sub}>
                        {m.id}
                        {m.email ? ` · ${m.email}` : ''}
                      </div>
                    </td>
                    <td data-col="role" data-label="Role">
                      <select
                        className={styles.select}
                        value={m.role}
                        disabled={self || busy === `role:${m.id}`}
                        title={self ? 'You cannot change your own role — ask another executive.' : undefined}
                        onChange={(e) => setRole(m, e.target.value)}
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_META[r].label}
                          </option>
                        ))}
                        {!ROLE_OPTIONS.includes(m.role) && (
                          <option value={m.role}>{m.role}</option>
                        )}
                      </select>
                    </td>
                    <td className={styles.sub} data-col="lane" data-label="Lane">{ROLE_META[m.role]?.lane || '—'}</td>
                    <td data-col="book" data-label="Assignments">
                      {m.role === ROLES.REP ? (
                        <button className={styles.link} onClick={() => setBookFor(m)}>
                          {m.book?.length ? `${m.book.length} assigned` : 'Derived'}
                        </button>
                      ) : (
                        <span className={styles.sub}>—</span>
                      )}
                    </td>
                    <td data-col="perm" data-label={MANAGE_ASSIGNMENTS_LABEL}>
                      <label className={styles.permToggle} title={perm.why || sourceLabel(perm.source)}>
                        <input
                          type="checkbox"
                          checked={perm.allowed}
                          disabled={self || busy === `perm:${m.id}`}
                          onChange={(e) => toggleAssignments(m, e.target.checked)}
                        />
                        <span className={styles.sub}>
                          {sourceLabel(perm.source)}
                          {!perm.persisted && <span className={styles.permWarn}> ⚠</span>}
                        </span>
                      </label>
                      {self && (
                        <div className={styles.sub}>
                          You can’t change your own grant — ask another executive.
                        </div>
                      )}
                      {grantedByBackend(m.permissions) && !perm.persisted && (
                        <div className={styles.sub}>Roster grant present.</div>
                      )}
                      {grantRow(m)?.isLaneLead && (
                        <div className={styles.laneGrant}>
                          <label className={styles.permToggle}>
                            <input
                              type="checkbox"
                              checked={grantRow(m)?.canDelegateLane === true}
                              disabled={busy === `lane:${m.id}`}
                              onChange={(e) => toggleLane(m, e.target.checked)}
                            />
                            <span>
                              <strong>{LANE_DELEGATE_LABEL}</strong>
                              <span className={styles.sub}>
                                {grantRow(m)?.canDelegateLane ? ' · on, saved to the grant store' : ' · off'}
                              </span>
                            </span>
                          </label>
                          <div className={styles.sub}>
                            Lets {m.name.split(' ')[0]} move {(grants.lane?.label || 'cheer').toLowerCase()} records
                            between {(grants.lane?.members || []).map((n) => n.split(' ')[0]).join(', ') || 'her lane'},
                            nothing else. Every move is logged automatically. A note is optional.
                          </div>
                        </div>
                      )}
                    </td>
                    <td data-col="status" data-label="Status">
                      <span className={styles.state} data-on={m.active}>
                        {m.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className={styles.right} data-col="cta" data-label="Actions">
                      <div className={styles.rowActions}>
                        <TeamMemberToolAccessLink member={m}/>
                        <button
                          className="btn"
                          disabled={self || busy === `active:${m.id}`}
                          onClick={() => setActive(m, !m.active)}
                        >
                          {m.active ? 'Deactivate' : 'Reactivate'}
                        </button>
                        <button
                          className="btn"
                          disabled={self || busy === `remove:${m.id}`}
                          onClick={() => {
                            if (window.confirm(`Remove ${m.name} from the roster? They lose access immediately.`)) remove(m)
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {grants.error && (
          <p className={styles.foot}>
            <strong>The grant store did not answer:</strong> {grants.error} The grant column is showing
            the older resolution until it does.
          </p>
        )}
        <p className={styles.foot}>
          Secured requests recheck the current roster, active status and role. Role changes and
          deactivation apply to those requests without waiting for a new sign-in. This does not
          cryptographically revoke an issued JWT. You cannot change your own role,
          and the last active executive cannot be demoted or deactivated.
        </p>
      </section>

      {/* THE ROLE-LEVEL SWITCH. The per-person column above is the normal
          control; this turns a whole lane on or off at once. It is always a
          SESSION override — there is no role-permission store on the backend,
          and the page says that rather than implying a durable change. */}
      <section className={`card ${styles.panel}`}>
        <div className="eyebrow">{MANAGE_ASSIGNMENTS_LABEL} · by role</div>
        <p className={styles.foot}>
          Who may reassign a rep in the Command Center — approve an engine proposal, pick a rep on a
          record, bulk-reassign a selection. Everyone else sees assignments read-only.{' '}
          <strong>Executives hold it from their role.</strong> A role switch here applies to this
          session only and is not persisted; a per-person grant in the table above is saved to the
          backend grant store and survives sign-out.
        </p>
        <div className={styles.roleGrants}>
          {TOGGLEABLE_ROLES.map((r) => {
            const g = resolveManageAssignments({
              user: null,
              role: r,
              policyAllows: rawCan(makeContext({ role: r }), MANAGE_ASSIGNMENTS),
              // `grantTick` is read here so the row re-resolves after a flip.
              overrides: undefined,
            })
            void grantTick
            return (
              <label key={r} className={styles.permToggle} title={g.why || sourceLabel(g.source)}>
                <input
                  type="checkbox"
                  checked={g.allowed}
                  onChange={(e) => toggleRoleAssignments(r, e.target.checked)}
                />
                <span>
                  <strong>{ROLE_LABEL(r)}</strong>
                  <span className={styles.sub}> · {sourceLabel(g.source)}</span>
                </span>
              </label>
            )
          })}
        </div>
      </section>

      <div className={styles.split}>
        <AddMember onDone={load} flash={flash} />
        <PasswordTool members={state.members} flash={flash} />
      </div>

      {bookFor && (
        <BookDrawer
          member={bookFor}
          onClose={() => setBookFor(null)}
          onSaved={async (text) => {
            await load()
            flash('ok', text)
          }}
        />
      )}
    </div>
  )
}

/* ---- Pieces ---------------------------------------------------------- */

function Stat({ label, value, sub }) {
  return (
    <div className={styles.stat}>
      <div className="eyebrow">{label}</div>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statSub}>{sub}</div>
    </div>
  )
}

const BLANK = { id: '', name: '', email: '', role: '', password: '' }

function AddMember({ onDone, flash }) {
  const [form, setForm] = useState(BLANK)
  const [saving, setSaving] = useState(false)
  const [pending, setPending] = useState(null)
  const [error, setError] = useState('')
  const inFlight = useRef(false)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const verify = async (expected) => {
    const member = verifiedTeamMember(await api.team(), expected)
    setPending(null)
    setForm(BLANK)
    setError('')
    await onDone()
    flash('ok', `${member.name}: main portal server account verified as ${ROLE_META[member.role]?.label}. Pricing access is separate. Have them test a fresh sign-in.`)
  }

  const checkPending = async () => {
    if (inFlight.current || !pending) return
    inFlight.current = true
    setSaving(true)
    try { await verify(pending) }
    catch (err) { setError(errText(err, 'Could not verify the server roster.')) }
    finally { inFlight.current = false; setSaving(false) }
  }

  const submit = async (e) => {
    e.preventDefault()
    if (inFlight.current || pending) return
    const id = form.id.trim().toLowerCase()
    if (!/^[a-z0-9_.-]+$/.test(id)) return setError('Use only letters, digits, dots, underscores or hyphens in the username.')
    if (!ROLE_OPTIONS.includes(form.role)) return setError('Choose the authorized main portal role.')
    if (form.password.length < 10) return setError('The initial password must be at least 10 characters.')
    const expected = { id, name: form.name.trim() || id, email: form.email.trim().toLowerCase(), role: form.role }
    const payload = { ...expected, password: form.password }
    inFlight.current = true
    setSaving(true)
    setError('')
    setForm((f) => ({ ...f, password: '' }))
    let accepted = false
    try {
      await api.teamAdd(payload)
      accepted = true
      await verify(expected)
    } catch (err) {
      if (accepted || !creationWasRejected(err)) {
        setPending(expected)
        setError('Creation may have reached the server, but the account is not verified. Check the server record below; do not submit it again. ' + errText(err, 'Verification failed.'))
      } else {
        setError(errText(err, 'Could not add that person.'))
      }
    } finally {
      delete payload.password
      inFlight.current = false
      setSaving(false)
    }
  }

  return (
    <section className={`card ${styles.panel}`}>
      <div className={styles.panelHead}>
        <div>
          <div className="eyebrow">Add personnel</div>
          <h2 className={`${styles.panelTitle} display`}>Create a main portal account</h2>
        </div>
      </div>
      <p className={styles.foot}>
        This creates an account for app.srchamp.com only. It does not create a pricing login or copy permissions.
        For pricing access, an authorized administrator must open{' '}
        <a href="https://pricing.srchamp.com" target="_blank" rel="noopener noreferrer">Pricing → Personnel</a>
        {' '}and create a separate server account with its separately approved pricing role.
      </p>
      {error && <p className={styles.errorText} role="alert">{error}</p>}
      {pending && <button className="btn" type="button" disabled={saving} onClick={checkPending}>Check server record (no new account)</button>}
      <form onSubmit={submit} className={styles.form}>
        <fieldset className={styles.form} disabled={saving || !!pending} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
        <div className={styles.fieldRow}>
          <label className={styles.field}>
            <span className="field-label">Username</span>
            <input className="input" value={form.id} onChange={set('id')} placeholder="firstlast" required />
          </label>
          <label className={styles.field}>
            <span className="field-label">Full name</span>
            <input className="input" value={form.name} onChange={set('name')} required />
          </label>
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.field}>
            <span className="field-label">Email</span>
            <input className="input" type="email" value={form.email} onChange={set('email')} placeholder="name@southernrecognition.com" />
          </label>
          <label className={styles.field}>
            <span className="field-label">Main portal role</span>
            <select className={`input ${styles.select}`} value={form.role} onChange={set('role')} required>
              <option value="">Choose the authorized role</option>
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {ROLE_META[r].label} — {ROLE_META[r].lane}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className={styles.field}>
          <span className="field-label">Initial password</span>
          <input
            className="input"
            type="password"
            value={form.password}
            onChange={set('password')}
            minLength={10}
            autoComplete="new-password"
            required
          />
          <span className={styles.hint}>
            At least 10 characters. It is hashed with bcrypt on the server and never stored,
            logged, or sent back — including into the audit trail.
          </span>
        </label>
        <div className={styles.formFoot}>
          <button className="btn btn-primary" disabled={saving}>
            {saving ? <span className="spinner" /> : <IconCheck width={15} height={15} />}
            Add person
          </button>
        </div>
        </fieldset>
      </form>
    </section>
  )
}

function PasswordTool({ members, flash }) {
  const [id, setId] = useState('')
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!id && members.length) setId(members[0].id)
  }, [members, id])

  const submit = async (e) => {
    e.preventDefault()
    if (password.length < 10) return flash('danger', 'The new password must be at least 10 characters.')
    setSaving(true)
    try {
      await api.teamPassword(id, password)
      setPassword('')
      flash('ok', `Password rotated for ${members.find((m) => m.id === id)?.name || id}.`)
    } catch (err) {
      flash('danger', errText(err, 'Could not set that password.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className={`card ${styles.panel}`}>
      <div className={styles.panelHead}>
        <div>
          <div className="eyebrow">Credentials</div>
          <h2 className={`${styles.panelTitle} display`}>Set or rotate a password</h2>
        </div>
      </div>
      <form onSubmit={submit} className={styles.form}>
        <label className={styles.field}>
          <span className="field-label">Person</span>
          <select className={`input ${styles.select}`} value={id} onChange={(e) => setId(e.target.value)}>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.id})
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span className="field-label">New password</span>
          <input
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={10}
            autoComplete="new-password"
            required
          />
        </label>
        <div className={styles.formFoot}>
          <button className="btn" disabled={saving || !id}>
            {saving ? <span className="spinner" /> : null}
            Set password
          </button>
        </div>
      </form>
      <p className={styles.foot}>
        The password is sent once, hashed server-side with bcrypt, and discarded. It is redacted
        before the audit observer sees it — the trail records that a rotation happened, never what
        was set.
      </p>
    </section>
  )
}

/**
 * The rep's BOOK. Two states, and the difference matters:
 *   DERIVED  — resolved live from the configured source (ClickUp today) every
 *              time they sign in. Self-maintaining.
 *   ASSIGNED — an explicit list an exec typed. It overrides the derived source
 *              completely and is never silently replaced by it.
 */
function BookDrawer({ member, onClose, onSaved }) {
  const [state, setState] = useState({ status: 'loading', book: null, error: '' })
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .teamBook(member.id)
      .then((res) => {
        if (cancelled) return
        setState({ status: 'ready', book: res, error: '' })
        setDraft((res.explicit || []).join('\n'))
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error', book: null, error: errText(err, 'Could not read the book.') })
      })
    return () => {
      cancelled = true
    }
  }, [member.id])

  const save = async (next) => {
    setSaving(true)
    try {
      await api.teamBookSet(member.id, next, { book: state.book?.explicit || [] })
      await onSaved(
        next.length
          ? `${member.name}'s book set to ${next.length} account(s).`
          : `${member.name}'s book is derived from ${state.book?.source || 'the live source'} again.`
      )
      onClose()
    } catch (err) {
      setState((s) => ({ ...s, error: errText(err, 'Could not save the book.') }))
    } finally {
      setSaving(false)
    }
  }

  const parsed = draft
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)

  return (
    <div className={styles.drawerScrim} role="dialog" aria-modal="true" onClick={onClose}>
      <div className={`card ${styles.drawer}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.panelHead}>
          <div>
            <div className="eyebrow">Rep assignments</div>
            <h2 className={`${styles.panelTitle} display`}>{member.name}</h2>
          </div>
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>

        {state.status === 'loading' && (
          <p className={styles.empty}>
            <span className="spinner" /> Resolving the assignments…
          </p>
        )}

        {state.book && (
          <>
            <div className={styles.bookMeta}>
              <span className="chip">source: {state.book.source}</span>
              <span className="chip">{state.book.ids?.length || 0} account(s)</span>
              {state.book.degraded && (
                <span className={styles.state} data-on={false}>
                  source unavailable
                </span>
              )}
            </div>
            <p className={styles.hint}>{state.book.detail}</p>
            <SchoolTeamBook coverage={state.book.schoolTeamCoverage}/>

            {!!state.book.accounts?.length && (
              <div className={styles.bookList}>
                {state.book.accounts.slice(0, 60).map((a) => (
                  <div key={a.id} className={styles.bookRow}>
                    <strong>{a.name || a.order || a.id}</strong>
                    <span className={styles.sub}>{a.id}</span>
                  </div>
                ))}
                {state.book.accounts.length > 60 && (
                  <div className={styles.sub}>…and {state.book.accounts.length - 60} more</div>
                )}
              </div>
            )}

            <label className={styles.field}>
              <span className="field-label">Assign explicitly (one account id or name per line)</span>
              <textarea
                className={`input ${styles.textarea}`}
                rows={6}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Leave empty to derive the assignments from the live source"
              />
              <span className={styles.hint}>
                An explicit list overrides the derived source entirely — nothing recalculates it
                behind you. Clear it to hand this rep back to {state.book.source === 'explicit' ? 'the live source' : state.book.source}.
              </span>
            </label>

            {state.error && (
              <p className={styles.errorText} role="alert">
                {state.error}
              </p>
            )}

            <div className={styles.formFoot}>
              <button className="btn" disabled={saving} onClick={() => save([])}>
                Clear override
              </button>
              <button className="btn btn-primary" disabled={saving} onClick={() => save(parsed)}>
                {saving ? <span className="spinner" /> : <IconCheck width={15} height={15} />}
                Assign {parsed.length || 0} account(s)
              </button>
            </div>
          </>
        )}

        {state.status === 'error' && !state.book && <p className={styles.errorText}>{state.error}</p>}
      </div>
    </div>
  )
}
